import { useState, useCallback, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2, Upload, ImageIcon, ExternalLink, Copy, CheckCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface GenerateClientProgrammeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  quoteShareToken: string;
  /** "quote" creates a branded total-investment quote instead of a programme. */
  kind?: "programme" | "quote";
}

export function GenerateClientProgrammeDialog({
  open,
  onOpenChange,
  quoteShareToken,
  kind = "programme",
}: GenerateClientProgrammeDialogProps) {
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [brandCompanyName, setBrandCompanyName] = useState("");
  const [preparedFor, setPreparedFor] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedUrl, setGeneratedUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [sourcePresentation, setSourcePresentation] = useState<string | null>(null);
  const [presentation, setPresentation] = useState<"marked_up_items" | "total_only">("total_only");
  const isQuote = kind === "quote";
  const noun = isQuote ? "quote" : "programme";

  useEffect(() => {
    if (!open || !quoteShareToken) return;
    let active = true;

    const loadDefaults = async () => {
      const { data } = await supabase.rpc("get_public_quote_lead_info", {
        p_share_token: quoteShareToken,
      });
      if (!active || !data || (data as any).error) return;
      const lead = data as { client_name?: string | null; group_reference?: string | null };
      setBrandCompanyName((current) => current || lead.client_name || "");
      setPreparedFor((current) => current || lead.group_reference || "");
    };
    if (isQuote) {
      void supabase.rpc("get_quote_cost_presentation", { p_share_token: quoteShareToken }).then(({ data }) => {
        if (!active) return;
        setSourcePresentation((data as string) || null);
        setPresentation(data === "marked_up_items" ? "marked_up_items" : "total_only");
      });
    }

    void loadDefaults();
    return () => {
      active = false;
    };
  }, [open, quoteShareToken, isQuote]);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onload = () => setLogoPreview(reader.result as string);
      reader.readAsDataURL(file);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "image/*": [".png", ".jpg", ".jpeg", ".svg", ".webp"] },
    maxFiles: 1,
    maxSize: 5 * 1024 * 1024,
  });

  const handleCopyLink = async () => {
    if (!generatedUrl) return;
    await navigator.clipboard.writeText(generatedUrl);
    setCopied(true);
    toast.success("Link copied to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  const handleClose = (open: boolean) => {
    if (!open) {
      setGeneratedUrl(null);
      setLogoFile(null);
      setLogoPreview(null);
      setBrandCompanyName("");
      setPreparedFor("");
      setCopied(false);
      setSourcePresentation(null);
    }
    onOpenChange(open);
  };

  const handleGenerate = async () => {
    setIsGenerating(true);
    try {
      let filePath: string | null = null;
      if (logoFile) {
        const fileExt = logoFile.name.split(".").pop();
        filePath = `client-logos/${crypto.randomUUID()}.${fileExt}`;

        const { error: uploadError } = await supabase.storage
          .from("entity-images")
          .upload(filePath, logoFile, { upsert: true });

        if (uploadError) {
          throw new Error("Failed to upload logo: " + uploadError.message);
        }
      }

      const args = {
        p_quote_share_token: quoteShareToken,
        p_client_logo_url: filePath,
        p_prepared_for: preparedFor.trim() || null,
        p_brand_company_name: brandCompanyName.trim() || null,
      };
      const { data, error } = isQuote
        ? await supabase.rpc("create_client_branded_quote_v2", { ...args, p_cost_presentation: presentation })
        : await supabase.rpc("create_client_branded_programme", args);

      if (error) throw error;

      const result = data as { success: boolean; share_token?: string; error?: string };

      if (!result.success) {
        throw new Error(result.error || `Failed to generate ${noun}`);
      }

      const programmeUrl = `${window.location.origin}/${isQuote ? "client-quote" : "client-programme"}/${result.share_token}`;
      setGeneratedUrl(programmeUrl);
      toast.success(isQuote ? "Branded quote generated!" : "Programme generated successfully!");
    } catch (err: any) {
      console.error("Programme generation failed:", err);
      toast.error(err.message || "Failed to generate programme");
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{generatedUrl ? (isQuote ? "Quote Ready!" : "Programme Ready!") : (isQuote ? "Generate Branded Quote" : "Generate Your Programme")}</DialogTitle>
          <DialogDescription>
            {generatedUrl
              ? `Your branded travel ${noun} has been generated.`
              : isQuote
              ? sourcePresentation === "marked_up_items"
                ? "Creates a client-ready quote. Choose item prices (no markup shown) or the total only. Upload your logo, or leave it blank for a white-label quote."
                : "Creates a client-ready quote showing only the total investment due — no item pricing. Upload your logo, or leave it blank for a white-label quote."
              : "Optionally upload your company logo to create a branded travel programme, or generate one without a logo."}
          </DialogDescription>
        </DialogHeader>

        {generatedUrl ? (
          <div className="space-y-4">
            <div className="flex items-center justify-center p-4">
              <CheckCircle className="h-10 w-10 text-green-500" />
            </div>
            <div className="flex gap-2">
              <Button className="flex-1" onClick={() => window.open(generatedUrl, "_blank")}>
                <ExternalLink className="h-4 w-4 mr-2" />
                {isQuote ? "View Quote" : "View Programme"}
              </Button>
              <Button variant="outline" className="flex-1" onClick={handleCopyLink}>
                {copied ? <CheckCircle className="h-4 w-4 mr-2" /> : <Copy className="h-4 w-4 mr-2" />}
                {copied ? "Copied" : "Copy Link"}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div
              {...getRootProps()}
              className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                isDragActive
                  ? "border-primary bg-primary/5"
                  : logoPreview
                  ? "border-primary/50 bg-primary/5"
                  : "border-muted-foreground/30 hover:border-primary/50"
              }`}
            >
              <input {...getInputProps()} />
              {logoPreview ? (
                <div className="space-y-3">
                  <img src={logoPreview} alt="Logo preview" className="max-h-24 mx-auto object-contain" />
                  <p className="text-sm text-muted-foreground">Click or drag to replace</p>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="mx-auto w-12 h-12 rounded-full bg-muted flex items-center justify-center">
                    {isDragActive ? (
                      <Upload className="h-6 w-6 text-primary" />
                    ) : (
                      <ImageIcon className="h-6 w-6 text-muted-foreground" />
                    )}
                  </div>
                  <div>
                    <p className="text-sm font-medium">
                      {isDragActive ? "Drop your logo here" : "Drag & drop your logo (optional)"}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">PNG, JPG, SVG or WebP (max 5MB)</p>
                  </div>
                </div>
              )}
            </div>
            {isQuote && sourcePresentation === "marked_up_items" && (
              <div className="space-y-2">
                <Label>Pricing shown</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Button type="button" variant={presentation === "marked_up_items" ? "default" : "outline"} onClick={() => setPresentation("marked_up_items")} disabled={isGenerating}>
                    Item prices
                  </Button>
                  <Button type="button" variant={presentation === "total_only" ? "default" : "outline"} onClick={() => setPresentation("total_only")} disabled={isGenerating}>
                    Total only
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Item prices show each line at its final price — markup is never shown.
                </p>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="brand-company-name">Your company name (optional)</Label>
              <Input
                id="brand-company-name"
                placeholder="Company shown with your logo"
                value={brandCompanyName}
                onChange={(e) => setBrandCompanyName(e.target.value.slice(0, 150))}
                maxLength={150}
                disabled={isGenerating}
              />
              <p className="text-xs text-muted-foreground">
                Shown as the document brand. Leave blank for a logo-only or white-label document.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="prepared-for">Prepared for (client name)</Label>
              <Input
                id="prepared-for"
                placeholder="Your client's name"
                value={preparedFor}
                onChange={(e) => setPreparedFor(e.target.value.slice(0, 150))}
                maxLength={150}
                disabled={isGenerating}
              />
              <p className="text-xs text-muted-foreground">
                Shown as “Prepared for …”. It defaults to the traveller or group reference.
              </p>
            </div>
            {logoFile && (
              <Button
                variant="ghost"
                size="sm"
                className="w-full"
                onClick={() => {
                  setLogoFile(null);
                  setLogoPreview(null);
                }}
                disabled={isGenerating}
              >
                Remove logo
              </Button>
            )}
            <Button onClick={handleGenerate} disabled={isGenerating} className="w-full" size="lg">
              {isGenerating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : logoFile ? (
                isQuote ? "Generate Branded Quote" : "Generate Branded Programme"
              ) : (
                isQuote ? "Generate White-Label Quote" : "Generate Programme Without Logo"
              )}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
